import { useCallback, useEffect, useRef, useState } from "react";

import type { SidebarTrackingSectionName } from "app/pages/sales-intelligence/sub-modules/right-sidebar/components/Sidebar/SidebarContainer";

import type { IframeMessageEvent, ExtensionAppMode } from "./utils";
import { getIframeUrl, setSetting, getSettings } from "./utils";

export const useExtensionIframe = ({
    domain,
    urlParams,
    onClose,
}: {
    domain?: string;
    urlParams: {
        mode: ExtensionAppMode;
        trackingSectionName?: SidebarTrackingSectionName;
    };
    onClose?: () => void;
}) => {
    const [iframeUrl, setIframeUrl] = useState("");

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const updateIframeUrl = useCallback(() => {
        getIframeUrl({ domain, urlParams }).then((url) => setIframeUrl(url));
    }, [domain, urlParams]);

    useEffect(() => {
        updateIframeUrl();
    }, [updateIframeUrl]);

    const messageHandler = useCallback(
        (event: MessageEvent<IframeMessageEvent>) => {
            if (!event.data) {
                return;
            }

            switch (event.data.message) {
                case "set sandbox": {
                    const sandbox = event.data.sandbox;
                    setSetting("sandbox", sandbox);
                    updateIframeUrl();
                    break;
                }
                case "set uiversion": {
                    // if you need to set a custom UI version, you must comment code in case "set uiversion"
                    setSetting("uiVersion", undefined);
                    setSetting("isCustomUiVersion", undefined);
                    updateIframeUrl();
                    break;
                }
                case "reset uiversion": {
                    setSetting("uiVersion", undefined);
                    setSetting("isCustomUiVersion", undefined);
                    updateIframeUrl();
                    break;
                }
                case "close": {
                    onClose?.();
                    break;
                }
            }
        },
        [onClose, updateIframeUrl],
    );

    useEffect(() => {
        window.addEventListener("message", messageHandler);

        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, [iframeRef, messageHandler]);

    const updateDomain = (newDomain: string, widgetId?: string) => {
        iframeRef.current?.contentWindow?.postMessage(
            { message: "set domain", domain: newDomain, url: window.location.toString(), widgetId },
            "*",
        );
    };

    const handleIframeLoad = async () => {
        const settings = await getSettings();

        // if you need to set a custom UI version, you must remove this code block and send uiVersion and isCustomUiVersion in postMessage
        if (settings.isCustomUiVersion || settings.uiVersion) {
            setSetting("uiVersion", undefined);
            setSetting("isCustomUiVersion", undefined);
        }

        iframeRef.current?.contentWindow?.postMessage(
            {
                message: "set settings storage",
                settingsStorage: { sandbox: settings.sandbox, isCustomUiVersion: undefined, uiVersion: undefined },
            },
            "*",
        );
    };

    return {
        iframeProps: iframeUrl
            ? {
                  src: iframeUrl,
                  ref: iframeRef,
                  onLoad: handleIframeLoad,
              }
            : null,
        updateDomain,
    };
};
